import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { HostedContentFilterPolicySchema } from './model';

const requiredArray: ValidatorFn = (control: AbstractControl) =>
    Array.isArray(control.value) && control.value.length > 0 ? null : { required: true };

export function setupConditionalValidators(form: FormGroup): void {
    const applyArrayRequired = (enableKey: string, listKey: string) => {
        const update = (enabled: boolean) => {
            const listControl = form.get(listKey);
            if (enabled) {
                listControl.setValidators([requiredArray]);
            } else {
                listControl.clearValidators();
            }
            listControl.updateValueAndValidity({ emitEvent: false });
        };
        form.get(enableKey).valueChanges.subscribe(update);
        update(form.get(enableKey).value);
    };

    const applyRequiredWhenAnyAction = (
        controlKey: string,
        actionKeys: string[],
        actionValue: string,
        extraValidators: ValidatorFn[] = [],
    ) => {
        const update = () => {
            const control = form.get(controlKey);
            const needed = actionKeys.some((key) => form.get(key)?.value === actionValue);
            if (needed) {
                control.setValidators([Validators.required, ...extraValidators]);
            } else {
                control.clearValidators();
            }
            control.updateValueAndValidity({ emitEvent: false });
        };
        actionKeys.forEach((key) => form.get(key)?.valueChanges.subscribe(update));
        update();
    };

    applyArrayRequired('EnableLanguageBlockList', 'LanguageBlockList');
    applyArrayRequired('EnableRegionBlockList', 'RegionBlockList');

    const actionKeys = ['SpamAction', 'HighConfidenceSpamAction', 'PhishSpamAction', 'BulkSpamAction'];
    applyRequiredWhenAnyAction('AddXHeaderValue', actionKeys, 'AddXHeader');
    applyRequiredWhenAnyAction('ModifySubjectValue', actionKeys, 'ModifySubject');
    applyRequiredWhenAnyAction('RedirectToRecipients', [...actionKeys, 'HighConfidencePhishAction'], 'Redirect');
    applyRequiredWhenAnyAction('TestModeBccToRecipients', ['TestModeAction'], 'BccMessage', [Validators.email]);
}

export function initForm(schema: HostedContentFilterPolicySchema, fb: FormBuilder): FormGroup {
    const p = schema?.contains?.properties;
    return fb.group({
        SpamAction: [p?.SpamAction?.const],
        SpamQuarantineTag: [p?.SpamQuarantineTag?.const ?? ''],
        AddXHeaderValue: [p?.AddXHeaderValue?.const ?? ''],
        ModifySubjectValue: [p?.ModifySubjectValue?.const ?? ''],
        RedirectToRecipients: [p?.RedirectToRecipients?.items?.const ?? ''],
        HighConfidenceSpamAction: [p?.HighConfidenceSpamAction?.const],
        HighConfidenceSpamQuarantineTag: [p?.HighConfidenceSpamQuarantineTag?.const ?? ''],
        PhishSpamAction: [p?.PhishSpamAction?.const],
        PhishQuarantineTag: [p?.PhishQuarantineTag?.const ?? ''],
        HighConfidencePhishAction: [p?.HighConfidencePhishAction?.const],
        HighConfidencePhishQuarantineTag: [p?.HighConfidencePhishQuarantineTag?.const ?? ''],
        BulkSpamAction: [p?.BulkSpamAction?.const],
        BulkQuarantineTag: [p?.BulkQuarantineTag?.const ?? ''],
        IntraOrgFilterState: [p?.IntraOrgFilterState?.const],
        InlineSafetyTipsEnabled: [p?.InlineSafetyTipsEnabled?.const],
        QuarantineRetentionPeriod: [
            p?.QuarantineRetentionPeriod?.const,
            [Validators.required, Validators.min(1), Validators.max(30)],
        ],
        BulkThreshold: [p?.BulkThreshold?.const, [Validators.required, Validators.min(1), Validators.max(9)]],
        IncreaseScoreWithBizOrInfoUrls: [p?.IncreaseScoreWithBizOrInfoUrls?.const],
        IncreaseScoreWithImageLinks: [p?.IncreaseScoreWithImageLinks?.const],
        IncreaseScoreWithNumericIps: [p?.IncreaseScoreWithNumericIps?.const],
        IncreaseScoreWithRedirectToOtherPort: [p?.IncreaseScoreWithRedirectToOtherPort?.const],
        MarkAsSpamEmptyMessages: [p?.MarkAsSpamEmptyMessages?.const],
        MarkAsSpamJavaScriptInHtml: [p?.MarkAsSpamJavaScriptInHtml?.const],
        MarkAsSpamObjectTagsInHtml: [p?.MarkAsSpamObjectTagsInHtml?.const],
        MarkAsSpamFramesInHtml: [p?.MarkAsSpamFramesInHtml?.const],
        MarkAsSpamEmbedTagsInHtml: [p?.MarkAsSpamEmbedTagsInHtml?.const],
        MarkAsSpamFormTagsInHtml: [p?.MarkAsSpamFormTagsInHtml?.const],
        MarkAsSpamWebBugsInHtml: [p?.MarkAsSpamWebBugsInHtml?.const],
        MarkAsSpamSensitiveWordList: [p?.MarkAsSpamSensitiveWordList?.const],
        MarkAsSpamSpfRecordHardFail: [p?.MarkAsSpamSpfRecordHardFail?.const],
        MarkAsSpamFromAddressAuthFail: [p?.MarkAsSpamFromAddressAuthFail?.const],
        MarkAsSpamNdrBackscatter: [p?.MarkAsSpamNdrBackscatter?.const],
        TestModeAction: [p?.TestModeAction?.const],
        TestModeBccToRecipients: [p?.TestModeBccToRecipients?.items?.enum?.[0] ?? ''],
        PhishZapEnabled: [p?.PhishZapEnabled?.const],
        SpamZapEnabled: [p?.SpamZapEnabled?.const],
        EnableLanguageBlockList: [p?.EnableLanguageBlockList?.const ?? false],
        LanguageBlockList: [p?.LanguageBlockList?.items?.enum ?? []],
        EnableRegionBlockList: [p?.EnableRegionBlockList?.const ?? false],
        RegionBlockList: [p?.RegionBlockList?.items?.enum ?? []],
        AllowedSenders: [p?.AllowedSenders?.items?.enum ?? []],
        AllowedSenderDomains: [p?.AllowedSenderDomains?.items?.enum ?? []],
        BlockedSenders: [p?.BlockedSenders?.items?.enum ?? []],
        BlockedSenderDomains: [p?.BlockedSenderDomains?.items?.enum ?? []],
    });
}
